import React, { useEffect } from 'react';
import ControlledField from './ControlledField';
import FloatingNumber from '../../../../components/input/FloatingNumber';
import { formatCurrency } from '../../../../utils/format';

// Shared renderer for two-column field arrays.
// - fields: array of items with keys (key/camelKey/label/value/lock)
// - control, FloatingInput, cleanNumber: passed to Controllers
// - ignoreKeys: optional array of raw keys to filter out
export function renderPaymentItem(item, { control, FloatingInput, cleanNumber, toBaseKey, handleCurrencyBlur, onFormatted, index, parentName }) {
  const baseKey = toBaseKey(item.camelKey || item.key || item.label || '');
  const isRate = item.rate !== undefined && item.rate !== null && item.rate !== '';
  const idKey = String((item.key || item.camelKey || item.label || baseKey || index || '') + '::' + String(item.value ?? '')).replace(/\s+/g, '_');

  // Helper to produce namespaced RHF names when rendering from a useFieldArray
  const namespaced = (prop) => (parentName && typeof index === 'number' ? `${parentName}[${index}].${prop}` : prop);
  //console salesPrice

  if (isRate) {
    const percentName = namespaced('rate');
    const dollarName = namespaced('value');
    return (
      <>
        <div className="calc-frm-fld form-group sign-of-percentage" key={`${percentName}-${idKey}`}>
          <ControlledField
            key={`${percentName}-${idKey}`}
            name={percentName}
            control={control}
            className="input-filled"
            defaultValue={item.rate ?? ''}
            InputComponent={FloatingNumber}
            label={`${item.label}`}
            placeholder=""
            type="text"
            cleanNumber={cleanNumber}
            handleCurrencyBlur={handleCurrencyBlur}
            onFormatted={onFormatted}
          />
        </div>
        <div className="calc-frm-fld form-group sign-of-amount" key={`${dollarName}-${idKey}`}>
          <ControlledField
            key={`${dollarName}-${idKey}`}
            name={dollarName}
            control={control}
            className="input-filled"
            defaultValue={item.value ?? ''}
            InputComponent={FloatingNumber}
            placeholder=""
            type="text"
            cleanNumber={cleanNumber}
            handleCurrencyBlur={handleCurrencyBlur}
            onFormatted={onFormatted}
          />
        </div>
      </>
    );
  }

  // fallback single input
  const name = namespaced('value') || baseKey;
  return (
    <>
      <div className="calc-frm-fld form-group" key={`${name}-${idKey}`}>
        <ControlledField
          key={`${name}-${idKey}`}
          name={name}
          control={control}
          defaultValue={item.value ?? ''}
          InputComponent={FloatingNumber}
          placeholder=""
          label={item.label}
          type="text"
          cleanNumber={cleanNumber}
          handleCurrencyBlur={handleCurrencyBlur}
          onFormatted={onFormatted}
        />
      </div>
      <div className="calc-frm-fld form-group" />
    </>
  );
}

export default function DynamicFieldsGrid({
  fields = [],
  control,
  cleanNumber,
  ignoreKeys = [],
  renderItem = null,
  getRowClass = null,
  fullRowPerItem = false,
  handleCurrencyBlur,
  onFormatted,
  parentName = null,
  getValues,
  setValue,
  salesPrice,
}) {
  const safeFields = Array.isArray(fields) ? fields : [];

  const ignoreSet = new Set((ignoreKeys || []).map((k) => (k || '').toString()));

  const toBaseKey = (raw) => {
    const s = String(raw || '')
      .replace(/[-_ ]+(.)/g, (m, g1) => g1.toUpperCase())
      .replace(/[^a-zA-Z0-9]/g, '');
    return s.charAt(0).toLowerCase() + s.slice(1);
  };

  const visible = safeFields.filter((f) => !ignoreSet.has((f.key || '').toString()));

  const toNumber = (raw) => Number(String(raw ?? '').replace(/[^0-9.-]+/g, '')) || 0;

  // Keep transfer_tax amount in sync with salesPrice changes.
  // transfer_tax uses a per-mille (‰) rate: amount = salesPrice * (rate / 1000)
  useEffect(() => {
    if (typeof setValue !== 'function') return;

    const sales = toNumber(salesPrice);
    if (!Number.isFinite(sales) || sales <= 0) return;

    for (const item of visible) {
      const specialBaseKey = toBaseKey(item.camelKey || item.key || item.label || '');
      const rawKey = (item.key || '').toString();
      const isTransferTaxKey = rawKey === 'transfer_tax' || specialBaseKey === 'transferTax';
      if (!isTransferTaxKey) continue;

      const origIndex = safeFields.indexOf(item);
      const valueName = parentName ? `${parentName}[${origIndex}].value` : specialBaseKey;
      const rateName = parentName ? `${parentName}[${origIndex}].rate` : `${specialBaseKey}Rate`;
      const amountName = parentName ? `${parentName}[${origIndex}].amount` : `${specialBaseKey}Amount`;

      let rateRaw;
      try {
        if (typeof getValues === 'function') {
          rateRaw = getValues(rateName);
          if (rateRaw === undefined || rateRaw === null || rateRaw === '') rateRaw = getValues(valueName);
        }
      } catch (e) {
        // ignore
      }
      if (rateRaw === undefined || rateRaw === null || rateRaw === '') rateRaw = item?.rate ?? item?.value ?? 0;

      const rate = toNumber(rateRaw);
      const amount = Number.isFinite(sales * (rate / 1000)) ? sales * (rate / 1000) : 0;
      const formatted = formatCurrency(Number(amount).toFixed(2));

      try {
        const current = typeof getValues === 'function' ? getValues(amountName) : undefined;
        if (String(current ?? '') === String(formatted)) continue;
      } catch (e) {
        // ignore
      }

      try {
        setValue(amountName, formatted, { shouldDirty: true, shouldValidate: false });
      } catch (err) {
        // ignore setValue errors if not provided
      }
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [salesPrice, safeFields, parentName]);

  if (safeFields.length === 0) return null;

  if (fullRowPerItem) {
    return (
      <>
        {visible.map((item, idx) => {
          const originalIndex = safeFields.indexOf(item);
          const rowClass = typeof getRowClass === 'function' ? getRowClass(item, originalIndex) : 'calc-frm-field aa';

          const idKey = String((item.key || item.camelKey || item.label || originalIndex) + '::' + String(item.value ?? '')).replace(/\s+/g, '_');
          return (
            <div className={rowClass} key={`dyn-fields-row-${originalIndex}-${idKey}`}>
              {typeof renderItem === 'function'
                ? renderItem(item, { control, FloatingInput: FloatingNumber, cleanNumber, toBaseKey, handleCurrencyBlur, onFormatted, index: originalIndex, parentName, getValues, setValue })
                : (
                  // default single-column fallback when no renderItem provided
                  (() => {
                    const baseKey = toBaseKey(item.camelKey || item.key || item.label || '');
                   // const isLocked = (item.lock || '').toString() === 'Y';
                    const origIndex = fields.indexOf(item);
                    const name = parentName ? `${parentName}[${origIndex}].value` : baseKey;
                    const idKey2 = String((item.key || item.camelKey || item.label || origIndex) + '::' + String(item.value ?? '')).replace(/\s+/g, '_');

                    
                    return (
                      <>
                        <div className="calc-frm-fld form-group sign-of-amount" key={`${name}-${idKey2}`}>
                          <ControlledField
                            key={`${name}-${idKey2}`}
                            name={name}
                            control={control}
                            defaultValue={item?.value ?? ''}
                            InputComponent={FloatingNumber}
                            placeholder=""
                            label={`${item.label}${item.unit === '$' ? ' $' : ''}`}
                            type="text"
                            //inputProps={{ disabled: isLocked }}
                            cleanNumber={cleanNumber}
                            handleCurrencyBlur={handleCurrencyBlur}
                            onFormatted={onFormatted}
                          />
                        </div>
                        <div className="calc-frm-fld form-group" />
                      </>
                    );
                  })()
                )}
            </div>
          );
        })}
      </>
    );
  }

  // retain index information so we can map into RHF array paths when parentName is provided
  const visibleWithIndex = visible.map((item, i) => ({ item, i }));
  const rows = [];
  for (let i = 0; i < visibleWithIndex.length; i += 2) rows.push(visibleWithIndex.slice(i, i + 2));

  return (
    <>
      {rows.map((pair, idx) => {
        const rowClass = typeof getRowClass === 'function' ? getRowClass(pair, idx) : 'calc-frm-field full-dvd';
        // build a stable row key derived from the items in the pair so changes to item identity force remount
              const rowIds = pair.map(({ item: it }) => String((it.key || it.camelKey || it.label || safeFields.indexOf(it)) + '::' + String(it.value ?? '')).replace(/\s+/g, '_')).join('-');
        return (
          <div className={rowClass} key={`dyn-fields-row-${idx}-${rowIds}`}>
            {pair.map(({ item, i }) => {
              // allow custom rendering per item if a renderItem hook is provided
              if (typeof renderItem === 'function') {
                return renderItem(item, { control, FloatingInput: FloatingNumber, cleanNumber, toBaseKey, handleCurrencyBlur, onFormatted, index: i, parentName, getValues, setValue });
              }

              const baseKey = toBaseKey(item.camelKey || item.key || item.label || '');
           //   const isLocked = (item.lock || '').toString() === 'Y';
              const origIndex = safeFields.indexOf(item);
              const valueName = parentName ? `${parentName}[${origIndex}].value` : baseKey;
              const amountName = parentName ? `${parentName}[${origIndex}].amount` : `${baseKey}Amount`;
              const idKey = String((item.key || item.camelKey || item.label || origIndex) + '::' + String(item.value ?? '')).replace(/\s+/g, '_');

              // determine which field (value or amount) we should render
              let renderName = valueName;
              let defaultVal = item?.value ?? '';

              // compute baseKey for special-case checks (e.g., transfer_tax)
              const specialBaseKey = toBaseKey(item.camelKey || item.key || item.label || '');
              const rawKey = (item.key || '').toString();
              const isTransferTaxKey = rawKey === 'transfer_tax' || specialBaseKey === 'transferTax';

              // For percent rows, we display the computed dollar amount in `amount`.
              // For transfer_tax, amount is derived from salesPrice and the per-mille rate.
              if (item.unit === '%' || isTransferTaxKey) {
                const formatted = (() => {
                  if (isTransferTaxKey) {
                    const sales = toNumber(salesPrice);
                    const rate = toNumber(item?.rate ?? item?.value ?? 0);
                    const amount = Number.isFinite(sales * (rate / 1000)) ? sales * (rate / 1000) : 0;
                    return formatCurrency(Number(amount).toFixed(2));
                  }
                  // percent items already provide a computed `amount`
                  return formatCurrency(item?.amount ?? '0');
                })();

                renderName = amountName;
                defaultVal = formatted;
              } else if (item.unit === '$') {
                // The editable input is bound to `.value` (what the user
                // actually types), not `.amount`/`.total` — but other
                // consumers (the PDF template, possibly others) read
                // `.amount`/`.total` directly. Mirror the edit into both so
                // they never go stale, instead of only ever reflecting
                // whatever the row's template default happened to be.
                renderName = valueName;
                defaultVal = item?.value ?? '0';
              }

              const totalName = parentName ? `${parentName}[${origIndex}].total` : `${baseKey}Total`;
              const handleFormatted = (name, formatted) => {
                onFormatted?.(name, formatted);
                if (item.unit === '$' && typeof setValue === 'function') {
                  setValue(amountName, formatted, { shouldDirty: true, shouldValidate: false });
                  setValue(totalName, formatted, { shouldDirty: true, shouldValidate: false });
                }
              };

              return (
                <div className="calc-frm-fld form-group sign-of-amount" key={`${renderName}-${idKey}`}>
                  <ControlledField
                    key={`${renderName}-${idKey}`}
                    name={renderName}
                    control={control}
                    defaultValue={defaultVal}
                    InputComponent={FloatingNumber}
                    placeholder=""
                    label={`${item.label}`}
                    type="text"
                    //inputProps={{ disabled: isLocked }}
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    onFormatted={handleFormatted}
                  />
                </div>
              );
            })}

            {pair.length === 1 && <div className="calc-frm-fld form-group" />}
          </div>
        );
      })}
    </>
  );
}
